/**
 * Utility functions for tenant identification and subdomain handling
 */
import { TenantInfo, tenantService } from '../services/tenantService';

/**
 * Extract subdomain from the current hostname
 * @returns The subdomain or null if on the main domain
 */
export function getSubdomain(): string | null {
  const hostname = window.location.hostname;

  // Handle subdomain.localhost format for local development
  if (hostname.endsWith('.localhost')) {
    return hostname.split('.')[0];
  }

  // For plain localhost or IP addresses, check localStorage for tenant ID
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    // For local development, we can use localStorage to simulate a subdomain
    const storedTenantId = localStorage.getItem('tenantId');
    if (storedTenantId) {
      return storedTenantId;
    }
    return null;
  }

  const parts = hostname.split('.');

  // Check if we have a subdomain (e.g., tenant.flowcxp.com)
  if (parts.length > 2 && parts[0] !== 'www' && parts[0] !== 'app') {
    return parts[0];
  }

  return null;
}

/**
 * Check if the current hostname has a tenant subdomain
 * @returns True if on a tenant subdomain
 */
export function isTenantSubdomain(): boolean {
  return getSubdomain() !== null;
}

/**
 * Get the base domain without subdomain
 * @returns The base domain (e.g., flowcxp.com)
 */
export function getBaseDomain(): string {
  const hostname = window.location.hostname;

  // Skip for localhost or IP addresses
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return hostname;
  }

  const parts = hostname.split('.');

  // If we have a subdomain, return the base domain
  if (parts.length > 2) {
    return parts.slice(-2).join('.');
  }

  return hostname;
}

/**
 * Navigate to a tenant subdomain
 * @param subdomain The tenant subdomain
 * @param path Optional path to navigate to
 */
export function navigateToTenant(subdomain: string, path: string = '/'): void {
  const protocol = window.location.protocol;
  const baseDomain = getBaseDomain();
  const port = window.location.port ? `:${window.location.port}` : '';

  // Check if baseDomain is an IP address
  const isIpAddress = /^\d+\.\d+\.\d+\.\d+$/.test(baseDomain);

  if (isIpAddress) {
    // For IP addresses, we can't use subdomain notation
    // Instead, we'll use a query parameter or path to indicate the tenant
    window.location.href = `${protocol}//${baseDomain}${port}${path}?tenant=${subdomain}`;
  } else {
    // Normal domain name, use subdomain notation
    window.location.href = `${protocol}//${subdomain}.${baseDomain}${port}${path}`;
  }
}

/**
 * Navigate to the main domain
 * @param path Optional path to navigate to
 */
export function navigateToMainDomain(path: string = '/'): void {
  const protocol = window.location.protocol;
  const baseDomain = getBaseDomain();
  const port = window.location.port ? `:${window.location.port}` : '';

  // Always add www prefix for production domain
  if (baseDomain === 'flowcxp.com') {
    window.location.href = `${protocol}//www.${baseDomain}${port}${path}`;
  } else {
    window.location.href = `${protocol}//${baseDomain}${port}${path}`;
  }
}

/**
 * Validate if a tenant subdomain exists
 * @param subdomain The tenant subdomain to validate
 * @returns Promise resolving to true if the subdomain exists, false otherwise
 */
export async function validateTenantSubdomain(subdomain: string): Promise<TenantInfo | null> {
  try {
    // Use the tenant service to check if the subdomain exists
    const tenantInfo = await tenantService.getTenantInfo(subdomain);

    // If we got tenant data, the tenant exists
    return tenantInfo;
  } catch (error) {
    console.error('Error validating tenant subdomain:', error);
    // If there was an error (e.g., 404), the tenant doesn't exist
    return null;
  }
}
